import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {Dispatch, ReactNode, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';
import {v4 as uuidv4} from 'uuid';

import {
  BASE_GRAY_COLOR,
  BASE_TEXT,
  BASIC_MARGIN,
  BASIC_VERTICAL_MARGIN,
  CLICKABLE,
  COL_PADDING_OFFSET,
  FULL_WIDTH_MODAL_RULE,
  MAX_WIDTH_100,
  WITHOUT_BUTTON_FOCUS_STYLING,
} from 'analyses/alignment/modals/StyleConstants';
import UseCurrentVersionTable from 'analyses/alignment/modals/UseCurrentVersionTable';
import {NucleotideType} from 'biojs/BioConstants';
import {getSanitizedBasesAndSanitizationErrors} from 'biojs/Util';
import {SequenceSummary, SequenceType} from 'constants/SequenceConstants';
import {getBaseCoercionWarningText} from 'molbio/MolBioUtil';
import {NucleotideTypeToggle} from 'molbio/NucleotideTypeToggle';
import {SearchContext} from 'search/SearchConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY} from 'ui-library/Colors';
import FileChooser from 'ui-library/FileChooser';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ChipSearchInput, {deprecatedSearchGet} from 'ui-library/inputs/ChipSearchInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {InputErrorMessageStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import {isEnterKey} from 'util/Keyboard';
import {ElementKeyboardEvent} from 'util/Types';

import {
  BioentityInput,
  CreateSequenceAlignmentModalAction,
  CreateSequenceAlignmentModalActionType,
  CreateSequenceAlignmentModalState,
  InputType,
  SequenceAnalysisInfo,
} from './createSequenceAlignmentsModalReducer';
import {getInputChip, isBioentityInput, isTemplateSetup} from './SharedUtil';

const ALIGNMENT_TEXT_INPUT_MAX_NAME_LENGTH = 255; // Should be kept in sync with FolderItemMixin.MAX_NAME_LENGTH

/**
 * Renders a collapsible section with a title and chevron button icon.
 * @param title Title of the section
 * @param children Children to show or hide based on the collapsed state.
 * @constructor
 */
export function CollapsibleSection({title, children}: {title: string; children?: ReactNode}): JSX.Element {
  const [isCollapsed, setCollapsed] = useState(false);
  return (
    <>
      <hr className={css(styles.fullWidthHorizontalRule)} />
      <div
        onClick={() => {
          setCollapsed(!isCollapsed);
        }}
        className={css(styles.clickable)}
      >
        <BenchlingIcon icon={isCollapsed ? 'chevron-right' : 'chevron-down'} className={css(styles.icon)} />
        <span className={css(styles.text)}>{title}</span>
      </div>
      {!isCollapsed && children}
    </>
  );
}

function maybeGetExistingConsensusNucleotideType(
  sequenceAnalysisInfo?: SequenceAnalysisInfo
): NucleotideType | undefined {
  if (sequenceAnalysisInfo != null && !isTemplateSetup(sequenceAnalysisInfo.alignmentSetup)) {
    return sequenceAnalysisInfo.alignmentSetup.nucleotideType;
  } else {
    return undefined;
  }
}

export default function ChooseInputPane({
  inputs,
  sequenceAnalysisInfo,
  dispatch,
}: {
  inputs: CreateSequenceAlignmentModalState['inputs'];
  sequenceAnalysisInfo?: SequenceAnalysisInfo;
  dispatch: Dispatch<CreateSequenceAlignmentModalAction>;
}): JSX.Element {
  const existingConsensusNucleotideType: NucleotideType | undefined =
    maybeGetExistingConsensusNucleotideType(sequenceAnalysisInfo);
  const [nucleotideType, setNucleotideType] = useState(existingConsensusNucleotideType ?? NucleotideType.DNA);
  const [textInputName, setTextInputName] = useState('');
  const [textInputBases, setTextInputBases] = useState('');
  const [query, setQuery] = useState('');
  const [basesCoerced, setBasesCoerced] = useState(false);
  const nameField = useRef<HTMLInputElement>(null);

  const eligibleToSubmitTextInput = Boolean(textInputName) && Boolean(textInputBases);

  function setSanitizedTextInputBases(bases: string, nucleotideType: NucleotideType): void {
    const {
      sanitizedBases,
      errors: {convertedUToT, convertedTToU},
    } = getSanitizedBasesAndSanitizationErrors(bases, nucleotideType);

    if (convertedUToT || convertedTToU) {
      setBasesCoerced(true);
    }
    setTextInputBases(sanitizedBases);
  }

  function submitTextInput(): void {
    dispatch({
      type: CreateSequenceAlignmentModalActionType.ADD_INPUT,
      input: {
        type: InputType.TEXT,
        name: textInputName,
        residues: textInputBases,
        id: uuidv4(),
        nucleotideType,
      },
    });
    setTextInputName('');
    setTextInputBases('');
    if (nameField.current) {
      nameField.current.focus();
    }
  }

  function makeInterceptEnterKeyFunc(callback: () => void): (e: ElementKeyboardEvent) => void {
    return (e: ElementKeyboardEvent) => {
      if (isEnterKey(e)) {
        // Prevent submitting the form which will advance to the next pane.
        e.preventDefault();
        callback();
      }
    };
  }

  let sequenceTypeFilter = 'long_sequence';
  if (existingConsensusNucleotideType != null) {
    sequenceTypeFilter = existingConsensusNucleotideType === NucleotideType.DNA ? 'sequence' : 'rna_sequence';
  }

  return (
    <>
      {(!existingConsensusNucleotideType || existingConsensusNucleotideType === NucleotideType.DNA) && (
        <>
          <div className={css(styles.text, styles.basicBottomMargin)}>
            Upload sequence and trace files (.ab1, .ftv, .fasta, .gb, and .geneious). RNA uploads are not
            currently supported.
          </div>
          <div className={css(styles.fileInputOffset)}>
            <FileChooser
              multiple={true}
              onSelectFiles={(...files: Array<File>) => {
                dispatch({type: CreateSequenceAlignmentModalActionType.UPLOAD_FILES, files, dispatch});
              }}
            />
          </div>
        </>
      )}
      <div className={css(styles.text, styles.basicVerticalMargin)}>Search for a DNA / RNA sequence.</div>
      <div className={classNames('u-flex-display', css(styles.colPaddingOffset))}>
        <Col lg={5} md={5} sm={5} xs={5}>
          <ChipSearchInput
            searchURL={`/1/api/all/search?filter=${sequenceTypeFilter}`}
            fetchParams={{context: SearchContext.LISTING}}
            doSearch={deprecatedSearchGet}
            isMulti={true}
            autoFocus={false}
            searchOnEmptyQuery={false}
            placeholder="Search by name"
            getOptionDatasFromResponse={(resp) =>
              resp.data.map((seq: SequenceSummary) => ({
                name: (
                  <span>
                    <div className={css(styles.ellipsizedText)}>{seq.name}</div>
                    <div className={css(styles.ellipsizedText, styles.optionMetadata)}>{seq.folder.name}</div>
                  </span>
                ),
                icon: seq.sequence_type === SequenceType.DOUBLE_STRANDED ? 'sequence' : 'rna_sequence',
                // Use the same value object it's already been selected as an input, so the user can't add the
                // same sequence twice and so the selected indicator in the dropdown works correctly
                value: inputs.find((i) => isBioentityInput(i) && i.id === seq.id) ?? {
                  type: InputType.BIOENTITY,
                  name: seq.name,
                  id: seq.id,
                  canBeTemplate: seq.permissions.polymerFeaturesEditable,
                  nucleotideType: seq.nucleotideType,
                },
                dataTestAttrs: {component: 'ChipSelectInput', element: 'searchResult', key: seq.id},
              }))
            }
            value={inputs}
            query={query}
            onQueryChange={(e) => {
              setQuery(e.target.value);
            }}
            onSelect={(input: BioentityInput) => {
              if (inputs.some((item) => isEqual(item, input))) {
                dispatch({type: CreateSequenceAlignmentModalActionType.REMOVE_INPUT, input});
              } else {
                dispatch({type: CreateSequenceAlignmentModalActionType.ADD_INPUT, input});
              }
            }}
            renderChips={() => null}
            scrollable={true}
            onKeyDown={makeInterceptEnterKeyFunc(noop)}
          />
        </Col>
      </div>
      <hr />
      <div className={css(styles.text, styles.basicVerticalMargin)}>
        Create a DNA / RNA sequence from scratch.
      </div>
      <Row>
        <Col lg={5} md={5} sm={5} xs={5}>
          <NucleotideTypeToggle
            nucleotideType={nucleotideType}
            onChangeSelection={(newNucleotideType) => {
              setNucleotideType(newNucleotideType);
              setSanitizedTextInputBases(textInputBases, newNucleotideType);
              setBasesCoerced(false);
            }}
            disabled={existingConsensusNucleotideType != null}
          />
        </Col>
      </Row>
      <FormGroup className={classNames('u-flex-display', css(styles.colPaddingOffset, styles.maxWidth100))}>
        <Col lg={3} md={3} sm={3} xs={3}>
          <TextField
            value={textInputName}
            onChange={(e) => {
              setTextInputName(e.target.value);
            }}
            labelStyle={FieldLabel.STYLES.HIDDEN}
            label="Name"
            placeholder="Name"
            onKeyDown={makeInterceptEnterKeyFunc(() => {
              if (eligibleToSubmitTextInput) {
                submitTextInput();
              }
            })}
            textInputRef={nameField}
            maxLength={ALIGNMENT_TEXT_INPUT_MAX_NAME_LENGTH}
            dataTestAttrs={{component: 'CreateSequenceAlignmentsModal', element: 'textNameInput'}}
          />
        </Col>
        <Col lg={8} md={8} sm={8} xs={8}>
          <TextField
            value={textInputBases}
            onChange={(e) => {
              setSanitizedTextInputBases(e.target.value, nucleotideType);
            }}
            labelStyle={FieldLabel.STYLES.HIDDEN}
            label="Bases"
            placeholder="Bases"
            error={
              basesCoerced
                ? {
                    message: getBaseCoercionWarningText(nucleotideType),
                    style: InputErrorMessageStyle.INFO,
                  }
                : null
            }
            onKeyDown={makeInterceptEnterKeyFunc(() => {
              if (eligibleToSubmitTextInput) {
                submitTextInput();
              }
            })}
            dataTestAttrs={{component: 'CreateSequenceAlignmentsModal', element: 'textBasesInput'}}
          />
        </Col>
        <Col lg={1} md={1} sm={1} xs={1}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
            text="Add"
            disabled={!eligibleToSubmitTextInput}
            onClick={submitTextInput}
            className={css(styles.withoutButtonFocusStyling)}
            dataTestAttrs={{component: 'CreateSequenceAlignmentsModal', element: 'textAddButton'}}
          />
        </Col>
      </FormGroup>
      {sequenceAnalysisInfo && (
        <CollapsibleSection title="Current sequences">
          <UseCurrentVersionTable alignmentSetup={sequenceAnalysisInfo.alignmentSetup} dispatch={dispatch} />
        </CollapsibleSection>
      )}
      <CollapsibleSection title={sequenceAnalysisInfo ? 'New sequences' : 'Sequences'}>
        <div>
          {inputs.map((input) =>
            getInputChip(input, {
              size: ChipSize.MINI,
              onRemove: () => {
                dispatch({type: CreateSequenceAlignmentModalActionType.REMOVE_INPUT, input});
              },
            })
          )}
        </div>
      </CollapsibleSection>
    </>
  );
}

const styles = StyleSheet.create({
  text: BASE_TEXT,
  basicBottomMargin: {marginBottom: BASIC_MARGIN},
  basicVerticalMargin: BASIC_VERTICAL_MARGIN,
  fileInputOffset: {margin: '0px -10px'},
  colPaddingOffset: COL_PADDING_OFFSET,
  fullWidthHorizontalRule: FULL_WIDTH_MODAL_RULE,
  icon: {...BASE_GRAY_COLOR, marginRight: '8px'},
  maxWidth100: MAX_WIDTH_100,
  clickable: CLICKABLE,
  withoutButtonFocusStyling: WITHOUT_BUTTON_FOCUS_STYLING,
  optionMetadata: {color: GRAY},
  ellipsizedText: {whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'},
});
